import React, { useRef, useState } from 'react'
import { Formik } from 'formik'
import axios from 'axios'
import { Form as RBForm } from 'react-bootstrap'
import classNames from 'classnames'
import { useParams } from 'react-router-dom'
import { useErrorService, useNotificationService } from '@abroad/components'
import Imgix from 'react-imgix'
import { InformationIcon } from '../assetsComponents'
import API from '../../utils/API'
import ExperienceInputField from '../form/ExperienceInputField'
import ExperienceCheckbox from '../form/ExperienceCheckbox'
import ImageCropModal from '../common/ImageCropModal'
import ExperienceButton from './ExperienceButton'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const FACULTY_VISIBILITY_TIP =
  "If unticked, faculty will be visible on prosperity page but not on the enrolled experience user's page."

const FacultyEditor = ({
  faculty,
  facultyIndex,
  onSave,
  onCancel,
  isSaving = false,
  onUploadingChange,
}) => {
  const imageRef = useRef()
  const inputResetRef = useRef(null)
  const { questId } = useParams()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showRemoveImageConfirm, setShowRemoveImageConfirm] = useState(false)
  const pendingRemoveRef = useRef(null)

  const setUploading = (value) => {
    setIsUploading(value)
    if (onUploadingChange) onUploadingChange(value)
  }

  const handlePosterClick = () => {
    if (imageRef.current) {
      imageRef.current.value = ''
      imageRef.current.click()
    }
  }

  const validateImageFile = (imageFile) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()
    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }

    if (
      imageFile.type === 'image/webp' ||
      imageFile.name.toLowerCase().endsWith('.webp')
    ) {
      Notification.showNotification(
        'WebP format is not supported. Please use JPG, JPEG, or PNG format.',
        'danger',
      )
      return false
    }
    return true
  }

  const openCropModal = (imageFile, resetInput) => {
    if (!imageFile) return
    if (!validateImageFile(imageFile)) {
      if (resetInput) resetInput()
      return
    }
    inputResetRef.current = resetInput || null
    setSelectedImageFile(imageFile)
    setShowImageCropModal(true)
  }

  const resetImageInput = () => {
    if (typeof inputResetRef.current === 'function') {
      inputResetRef.current()
    }
    inputResetRef.current = null
  }

  const handleCropModalClose = () => {
    setShowImageCropModal(false)
    setSelectedImageFile(null)
    setIsDraggingImage(false)
    resetImageInput()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDraggingImage) setIsDraggingImage(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingImage(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      openCropModal(files[0])
      e.dataTransfer.clearData()
    }
  }

  const uploadFacultyImage = async (imageFile) => {
    try {
      const contentType = imageFile.name
        .substr(imageFile.name.lastIndexOf('.') + 1)
        .toUpperCase()

      const { data } = await API.AdminAPI.quests.getQuestImage(
        questId,
        contentType,
      )
      if (data) {
        await axios.put(data.url.url, imageFile, {
          headers: {
            'Content-Type': data.url.contentType,
          },
        })
        return data.fileName
      } else {
        throw new Error('Image upload failed')
      }
    } catch (e) {
      throw e
    }
  }

  const renderInfoIcon = () => {
    return (
      <span
        className='d-inline-flex align-items-center'
        data-tip={FACULTY_VISIBILITY_TIP}
        data-tip-pos='top'
        tabIndex={0}
        role='img'
        aria-label={FACULTY_VISIBILITY_TIP}>
        <InformationIcon iconClasses='align-self-center ml-2' />
      </span>
    )
  }

  const initialValues = {
    name: faculty?.name || '',
    title: faculty?.title || '',
    expertise: faculty?.expertise || '',
    description: faculty?.description || '',
    imageFileName: faculty?.imageFileName || '',
    isVisible: faculty?.isVisible !== undefined ? faculty.isVisible : true,
    sequenceNumber: faculty?.sequenceNumber || facultyIndex + 1,
    isUploading: false,
  }

  const handleFormSubmit = async (values) => {
    try {
      if (onSave) {
        onSave({
          ...values,
          isUploading: undefined,
        })
      }
    } catch (e) {
      Error.showError(e)
    }
  }

  const handleRemoveImage = async (setFieldValue) => {
    try {
      setFieldValue('imageFileName', '')
    } catch (e) {
      Notification.showNotification('Failed to remove image', 'danger')
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleFormSubmit}>
      {(formik) => {
        const { handleSubmit, setFieldValue, values } = formik
        const hasImage = !!values.imageFileName
        const busy = values?.isUploading || isUploading

        return (
          <form
            id='faculty-editor-form'
            onSubmit={handleSubmit}
            aria-busy={busy || undefined}>
            <div className='experience-fac-editor'>
              <div>
                <div className='experience-f-row'>
                  <ExperienceInputField
                    name='name'
                    label='Name'
                    placeholder='Add name'
                    capitalizeFirstLetter={true}
                    showErrorMessage={false}
                    displayFlex={false}
                    disabled={busy}
                  />
                  <ExperienceInputField
                    name='title'
                    label='Title'
                    placeholder='Add title'
                    capitalizeFirstLetter={true}
                    showErrorMessage={false}
                    displayFlex={false}
                    disabled={busy}
                  />
                </div>
                <div className='mt-12px'>
                  <ExperienceInputField
                    name='expertise'
                    label='Expertise'
                    placeholder='Add expertise'
                    capitalizeFirstLetter={true}
                    showErrorMessage={false}
                    displayFlex={false}
                    disabled={busy}
                  />
                </div>
                <div className='mt-12px'>
                  <ExperienceInputField
                    name='description'
                    label='Description'
                    placeholder='Add a short description'
                    as='textarea'
                    capitalizeFirstLetter={true}
                    rows={5}
                    showErrorMessage={false}
                    displayFlex={false}
                    disabled={busy}
                  />
                </div>
                <div className='mt-12px'>
                  <ExperienceCheckbox
                    name='isVisible'
                    checked={!!values.isVisible}
                    label='Is this an experiences faculty?'
                    extra={renderInfoIcon()}
                    disabled={busy}
                    onChange={(e) => {
                      if (busy) {
                        return
                      }
                      setFieldValue('isVisible', e.target.checked)
                    }}
                  />
                </div>
              </div>

              <div className='experience-f'>
                <label className='experience-f-label'>Faculty image</label>
                <div className='experience-img-slot' data-ratio='1:1'>
                  <div
                    className={classNames('experience-img-frame', {
                      'is-empty': !hasImage && !busy,
                      'is-dragging': isDraggingImage,
                    })}
                    onClick={() => {
                      if (!busy) handlePosterClick()
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (!busy) handlePosterClick()
                      }
                    }}>
                    {busy ? (
                      <span
                        className='experience-img-hover'
                        style={{ opacity: 1 }}>
                        Uploading…
                      </span>
                    ) : (
                      <span className='experience-img-hover'>
                        {isDraggingImage
                          ? 'Drop image to upload'
                          : hasImage
                          ? 'Click to replace'
                          : 'Upload image'}
                      </span>
                    )}

                    {hasImage ? (
                      values.imageFileName.startsWith('data:') ? (
                        <img src={values.imageFileName} alt='faculty' />
                      ) : (
                        <Imgix
                          src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${values.imageFileName}`}
                          imgixParams={{
                            fit: 'crop',
                            crop: 'faces',
                            auto: 'format,compress',
                          }}
                          width={'100%'}
                          alt='faculty'
                          className={classNames('lazyload', {
                            itenaryImage: busy,
                          })}
                          htmlAttributes={{
                            style: {
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            },
                          }}
                        />
                      )
                    ) : !busy ? (
                      <>
                        <b>Upload image</b>
                        <small>JPG, JPEG or PNG · cropped to 1:1</small>
                      </>
                    ) : null}

                    {hasImage && !busy ? (
                      <button
                        type='button'
                        className='experience-img-remove'
                        aria-label='Remove image'
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          pendingRemoveRef.current = () =>
                            handleRemoveImage(setFieldValue)
                          setShowRemoveImageConfirm(true)
                        }}>
                        ×
                      </button>
                    ) : null}

                    <RBForm.Control
                      type='file'
                      size='lg'
                      placeholder='Select File'
                      accept='image/jpg, image/jpeg, image/png'
                      onChange={async (e) => {
                        const target = e.target
                        const files = e.target.files
                        if (files && files?.length > 0) {
                          openCropModal(files[0], () => {
                            if (target) target.value = ''
                          })
                        }
                      }}
                      style={{ display: 'none' }}
                      name='imageFileName'
                      sm='10'
                      ref={imageRef}
                    />
                  </div>
                </div>
              </div>
            </div>

            <ImageCropModal
              show={showImageCropModal}
              onHide={handleCropModalClose}
              file={selectedImageFile}
              onConfirm={async (croppedImageFile) => {
                if (!croppedImageFile) return
                setShowImageCropModal(false)
                setFieldValue('isUploading', true)
                setUploading(true)
                const reader = new FileReader()
                reader.onloadend = async () => {
                  setFieldValue('imageFileName', reader.result)
                  try {
                    const fileName = await uploadFacultyImage(croppedImageFile)
                    setFieldValue('imageFileName', fileName)
                    setFieldValue('isUploading', false)
                    setUploading(false)
                  } catch (e) {
                    setFieldValue('isUploading', false)
                    setUploading(false)
                    Notification.showNotification(
                      'Image upload failed',
                      'danger',
                    )
                  } finally {
                    setSelectedImageFile(null)
                    setIsDraggingImage(false)
                    resetImageInput()
                  }
                }
                reader.readAsDataURL(croppedImageFile)
              }}
              isUploading={values?.isUploading}
              aspect={1}
              ratioLabel='1:1'
              title='Crop Image'
            />

            <div className='experience-form-actions'>
              <ExperienceButton
                type='submit'
                variant='primary'
                size='sm'
                isLoading={isSaving}
                disabled={isSaving || busy}>
                Save faculty
              </ExperienceButton>
              <ExperienceButton
                type='button'
                variant='outline'
                size='sm'
                onClick={onCancel}
                disabled={isSaving || busy}>
                Cancel
              </ExperienceButton>
            </div>
            <ExperienceConfirmModal
              show={showRemoveImageConfirm}
              onHide={() => {
                setShowRemoveImageConfirm(false)
                pendingRemoveRef.current = null
              }}
              onConfirm={() => {
                const run = pendingRemoveRef.current
                pendingRemoveRef.current = null
                setShowRemoveImageConfirm(false)
                if (run) run()
              }}
              title='Remove this image?'
              message='The image will be cleared. You can upload another.'
              confirmLabel='Delete'
            />
          </form>
        )
      }}
    </Formik>
  )
}

export default FacultyEditor
